import { useMemo, useState } from 'react';
import { useT } from '../../i18n/i18n';
import { linkExistingEvidenceAction } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { inspectionStore } from '../../state/inspectionStore';
import { buildPhotoPool, entryIsLinked, filterPool, poolCandidatesFor, POOL_FILTER_ORDER, type PoolFilter } from '../../state/photoPool';
import { Button, FilterChips, SheetShell } from '../../ui/components';
import { Thumbnail } from '../evidence/PhotoStrip';

// "照片池" picker (task book §9): from any item / finding, choose photos already in the inspection's
// pool and link them — multi-select, All / Unlinked / Linked, one photo may be linked to several
// targets. Linking only adds evidence links; nothing is moved or copied and the photo stays in the
// pool. For a target that does not exist yet (an unsaved finding) the ids are handed back and
// linked when the record is written.
export function PhotoPoolPickerSheet(props: {
  inspectionId: string;
  itemId: string | null;
  findingId: string | null;
  linkedIds: readonly string[];
  onClose: () => void;
  onLinked?: (ids: string[]) => void;
  layer?: 'base' | 'over' | 'top';
}) {
  const { t, tp } = useT();
  const evidenceState = useStore(evidenceStore, (s) => s);
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const [filter, setFilter] = useState<PoolFilter>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const candidates = useMemo(
    () => poolCandidatesFor(buildPhotoPool(props.inspectionId, evidenceState, inspectionState, findingState), { itemId: props.itemId, findingId: props.findingId, linkedIds: props.linkedIds }),
    [props.inspectionId, props.itemId, props.findingId, props.linkedIds, evidenceState, inspectionState, findingState]
  );
  const visible = useMemo(() => filterPool(candidates, filter), [candidates, filter]);
  const counts = { all: candidates.length, unlinked: candidates.filter((e) => !entryIsLinked(e)).length, linked: candidates.filter(entryIsLinked).length };

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleLink() {
    setBusy(true);
    try {
      if (props.itemId || props.findingId) {
        for (const id of selected) await linkExistingEvidenceAction(id, { inspectionId: props.inspectionId, itemId: props.itemId, findingId: props.findingId });
      }
      props.onLinked?.(selected);
      props.onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell
      title={t('pool.pickTitle')}
      subtitle={tp('common.photosCount', candidates.length)}
      onClose={props.onClose}
      layer={props.layer ?? 'over'}
      testId="photo-pool-picker"
      footer={
        <>
          <Button onClick={() => void handleLink()} disabled={busy || selected.length === 0} testId="pool-link-confirm">
            {t('pool.linkSelected', { n: selected.length })}
          </Button>
          <Button variant="text" onClick={props.onClose} testId="pool-pick-cancel">
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      <p className="card-note">{t('pool.pickBody')}</p>
      <FilterChips
        options={POOL_FILTER_ORDER.map((key) => ({ key, label: t(key === 'all' ? 'pool.filterAll' : key === 'unlinked' ? 'pool.filterUnlinked' : 'pool.filterLinked'), count: counts[key] }))}
        value={filter}
        onChange={setFilter}
      />
      {visible.length === 0 ? (
        <p className="card-note" data-testid="pool-pick-empty">
          {candidates.length === 0 ? t('pool.pickEmpty') : t('area.noMatch')}
        </p>
      ) : (
        <div className="photo-row photo-select-grid" data-testid="pool-pick-grid">
          {visible.map((entry) => (
            <div className="pool-pick-card" key={entry.evidence.id} data-testid="pool-pick-card" data-linked={entryIsLinked(entry) ? 'true' : 'false'}>
              <Thumbnail evidence={entry.evidence} selected={selected.includes(entry.evidence.id)} onClick={() => toggle(entry.evidence.id)} />
              {entryIsLinked(entry) ? <p className="pool-links-line">{tp('pool.linkedCount', entry.links.length)}</p> : null}
            </div>
          ))}
        </div>
      )}
    </SheetShell>
  );
}
